import { useState, useMemo } from 'react'

export interface ClientPaginationState {
  currentPage: number
  perPage: number
}

interface UseClientPaginationHook<T> {
  currentPage: number
  perPage: number
  totalCount: number
  totalPages: number
  paginatedData: T[]
  setPage: (page: number) => void
  setPerPage: (perPage: number) => void
}

interface UseClientPaginationParams {
  initialPerPage?: number
}

export function useClientPagination<T>(
  data: T[],
  { initialPerPage = 10 }: UseClientPaginationParams = {}
): UseClientPaginationHook<T> {
  const [paginationState, setPaginationState] = useState<ClientPaginationState>({
    currentPage: 1,
    perPage: initialPerPage
  })

  const totalCount = data.length
  const totalPages = Math.ceil(totalCount / paginationState.perPage)

  const paginatedData = useMemo(() => {
    const startIndex = (paginationState.currentPage - 1) * paginationState.perPage
    const endIndex = startIndex + paginationState.perPage
    return data.slice(startIndex, endIndex)
  }, [data, paginationState.currentPage, paginationState.perPage])

  const setPage = (page: number) => {
    setPaginationState(prev => ({
      ...prev,
      currentPage: Math.max(1, Math.min(page, totalPages))
    }))
  }

  const setPerPage = (perPage: number) => {
    setPaginationState(prev => ({
      currentPage: 1, // Reset to first page when changing page size
      perPage
    }))
  }

  return {
    currentPage: paginationState.currentPage,
    perPage: paginationState.perPage,
    totalCount,
    totalPages,
    paginatedData,
    setPage,
    setPerPage
  }
}
